import { io } from 'socket.io-client';
import { debugLog, debugWarn, debugError } from '../utils/logger';

const getSocketUrl = () => {
  if (process.env.REACT_APP_SOCKET_URL) {
    debugLog('🔑 Using REACT_APP_SOCKET_URL:', process.env.REACT_APP_SOCKET_URL);
    return process.env.REACT_APP_SOCKET_URL;
  }
  
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    debugLog('💻 Localhost detected, using http://localhost:5000');
    return 'http://localhost:5000';
  }
  
  const url = `https://${window.location.hostname}`;
  debugLog('🌍 Production mode, using:', url);
  return url;
};

const SOCKET_URL = getSocketUrl();
debugLog('🔌 Socket.io URL:', SOCKET_URL);

// Get token from localStorage
const getToken = () => {
  const token = localStorage.getItem('token');
  debugLog('🎫 Token exists:', !!token, token ? `(${token.substring(0, 20)}...)` : '(none)');
  return token;
};

// Create socket instance with JWT authentication
export const socket = io(SOCKET_URL, {
  autoConnect: false,
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  // Infinity y no 5: en móvil el socket cae cada vez que el usuario cambia de
  // app. Con 5 intentos (~15 s) la conexión se rendía DEFINITIVAMENTE y solo
  // volvía recargando la página; el usuario se quedaba sin timbre ni chat sin
  // notarlo.
  reconnectionAttempts: Infinity,
  transports: ['websocket', 'polling'],
  secure: true,
  rejectUnauthorized: false,
  auth: {
    token: getToken()
  }
});

debugLog('🏭 Socket instance created with config:', {
  autoConnect: false,
  reconnection: true,
  reconnectionAttempts: Infinity,
  transports: ['websocket', 'polling'],
  url: SOCKET_URL
});

// Function to connect with current token
export const connectSocket = () => {
  debugLog('\n🔄 [connectSocket] Attempting to connect...');
  const token = getToken();
  if (!token) {
    debugWarn('⚠️  [connectSocket] No token found, socket will not connect');
    return;
  }
  
  socket.auth = { token };
  debugLog('🔐 [connectSocket] Auth token updated');
  
  if (!socket.connected) {
    debugLog('🚀 [connectSocket] Calling socket.connect()...');
    socket.connect();
  } else {
    debugLog('✅ [connectSocket] Already connected, socket ID:', socket.id);
  }
};

// Function to disconnect socket
export const disconnectSocket = () => {
  debugLog('\n🚪 [disconnectSocket] Disconnecting socket...');
  if (socket.connected) {
    socket.disconnect();
    debugLog('✅ [disconnectSocket] Socket disconnected');
  } else {
    debugLog('ℹ️ [disconnectSocket] Socket was not connected');
  }
};

// Function to register user
export const registerUser = () => {
  debugLog('\n👤 [registerUser] Attempting to register...');
  if (socket.connected) {
    debugLog('✅ [registerUser] Socket connected, emitting user:register');
    socket.emit('user:register');
  } else {
    debugWarn('⚠️  [registerUser] Socket not connected, cannot register user');
    debugLog('📊 [registerUser] Socket state:', {
      connected: socket.connected,
      disconnected: socket.disconnected,
      id: socket.id
    });
  }
};

// ========== SOCKET EVENT LISTENERS ==========

socket.on('connect', () => {
  debugLog('\n✅ [EVENT: connect] Socket.io conectado:', socket.id);
  debugLog('📊 [connect] Transport:', socket.io.engine.transport.name);
  debugLog('🔗 [connect] Connected to:', socket.io.uri);
  
  registerUser();
});

socket.on('disconnect', (reason) => {
  debugLog('\n❌ [EVENT: disconnect] Socket.io desconectado');
  debugLog('📊 [disconnect] Reason:', reason);
  debugLog('📊 [disconnect] Was connected:', socket.connected);
  debugLog('🕒 [disconnect] Timestamp:', new Date().toISOString());
});

socket.on('connect_error', (error) => {
  debugError('\n🛑 [EVENT: connect_error] Error de conexión socket');
  debugError('❌ [connect_error] Message:', error.message);
  debugError('📊 [connect_error] Type:', error.type);
  debugError('📊 [connect_error] Description:', error.description);
  debugError('🕒 [connect_error] Timestamp:', new Date().toISOString());
  
  if (error.message.includes('Authentication') || error.message.includes('Invalid token')) {
    debugLog('🔄 [connect_error] Intentando reconectar con nuevo token...');
    const token = getToken();
    if (token) {
      socket.auth = { token };
      debugLog('✅ [connect_error] Token actualizado');
    } else {
      debugError('❌ [connect_error] No se encontró token para reconectar');
    }
  }
});

socket.on('reconnect_attempt', (attempt) => {
  // Releer el token en CADA intento: con access tokens de 15 minutos, el que
  // llevaba el socket al conectarse puede haber caducado y haber sido renovado
  // por el interceptor de api.js. Sin esto el socket reintentaría en bucle con
  // una credencial muerta.
  const token = getToken();
  if (token) socket.auth = { token };

  debugLog(`\n🔄 [EVENT: reconnect_attempt] Intento de reconexión #${attempt}`);
  debugLog('📊 [reconnect_attempt] Max attempts:', socket.io.opts.reconnectionAttempts);
});

socket.on('reconnect', (attemptNumber) => {
  debugLog('\n✅ [EVENT: reconnect] Reconectado exitosamente');
  debugLog('📊 [reconnect] After attempts:', attemptNumber);
  debugLog('🆔 [reconnect] New socket ID:', socket.id);
});

socket.on('reconnect_error', (error) => {
  debugError('\n❌ [EVENT: reconnect_error] Error en reconexión');
  debugError('📊 [reconnect_error]:', error.message);
});

socket.on('reconnect_failed', () => {
  debugError('\n🚫 [EVENT: reconnect_failed] Fallos de reconexión agotados');
  debugError('📊 [reconnect_failed] Max attempts reached');
});

socket.on('user:registered', (data) => {
  debugLog('\n🎉 [EVENT: user:registered] Usuario registrado en socket');
  debugLog('📊 [user:registered] Data:', data);
});

socket.on('user:error', (error) => {
  debugError('\n❌ [EVENT: user:error] Error de usuario en socket');
  debugError('📊 [user:error]:', error.message);
});

// Video call event logs
socket.on('incomingCall', (data) => {
  debugLog('\n📞 [EVENT: incomingCall] Llamada entrante recibida');
  debugLog('📊 [incomingCall] From:', data.from, 'Name:', data.fromName);
  debugLog('📊 [incomingCall] Signal present:', !!data.signal);
});

socket.on('callAccepted', (data) => {
  debugLog('\n✅ [EVENT: callAccepted] Llamada aceptada');
  debugLog('📊 [callAccepted] Signal present:', !!data.signal);
});

socket.on('callEnded', () => {
  debugLog('\n📴 [EVENT: callEnded] Llamada finalizada por el otro usuario');
});

socket.on('roomUsersUpdate', (data) => {
  debugLog('\n👥 [EVENT: roomUsersUpdate] Actualización de usuarios en sala');
  debugLog('📊 [roomUsersUpdate] Users count:', data.usersCount);
});

socket.on('video:users', (data) => {
  debugLog('\n👥 [EVENT: video:users] Usuarios en sala de video');
  debugLog('📊 [video:users] Count:', data.count);
  debugLog('📊 [video:users] Users:', data.users);
});

socket.on('video:error', (error) => {
  debugError('\n❌ [EVENT: video:error] Error de video');
  debugError('📊 [video:error]:', error.message);
});

export default socket;
